export default class Tooltip {
	constructor($module) {
		this.$module = $module;
		this.$toggle = $module.querySelector(".bc-tooltip__toggle");
		this.$tooltip = $module.querySelector(".bc-tooltip__body");
		this.$tooltipClose = this.$tooltip?.querySelector(".bc-tooltip__close");
		this.isOpen = false;

		if (!this.$toggle || !this.$tooltip) {
			return;
		}

		this.closeOnViewportChange = () => {
			this.close();
			this.$toggle.blur();
		};
		this.onEscape = (event) => {
			if (event.key === "Escape") {
				this.close();
			}
		};

		this.$toggle.setAttribute("aria-describedby", this.$tooltip.id);
		this.$tooltip.setAttribute("role", "tooltip");

		this.$module.addEventListener("mouseenter", () => this.open());
		this.$module.addEventListener("mouseleave", () => this.close());
		this.$module.addEventListener("focusin", () => this.open());
		this.$module.addEventListener("focusout", (event) => {
			if (!this.$module.contains(event.relatedTarget)) {
				this.close();
			}
		});

		if (this.$tooltipClose) {
			this.$tooltipClose.addEventListener("click", () => this.close());
		}

		this.close();
	}

	open() {
		if (this.isOpen) {
			return;
		}

		this.isOpen = true;
		this.$module.classList.add("bc-tooltip--open");
		this.$toggle.setAttribute("aria-expanded", "true");
		this.$tooltip.setAttribute("aria-hidden", "false");

		this.positionTooltip();
		document.addEventListener("keydown", this.onEscape, true);
		window.addEventListener("resize", this.closeOnViewportChange, true);
		window.addEventListener("scroll", this.closeOnViewportChange, true);
	}

	close() {
		this.isOpen = false;
		this.$module.classList.remove("bc-tooltip--open");
		this.$toggle.setAttribute("aria-expanded", "false");
		this.$tooltip.setAttribute("aria-hidden", "true");
		this.$tooltip.removeAttribute("data-placement");
		this.$tooltip.style.removeProperty("top");
		this.$tooltip.style.removeProperty("left");
		this.$tooltip.style.removeProperty("--bc-tooltip-arrow-x");
		this.$tooltip.style.removeProperty("--bc-tooltip-arrow-y");
		document.removeEventListener("keydown", this.onEscape, true);
		window.removeEventListener("resize", this.closeOnViewportChange, true);
		window.removeEventListener("scroll", this.closeOnViewportChange, true);
	}

	positionTooltip() {
		if (!this.isOpen) {
			return;
		}

		const offset = 8;
		const padding = 8;
		const arrowPadding = 12;
		const viewport = {
			top: padding,
			right: window.innerWidth - padding,
			bottom: window.innerHeight - padding,
			left: padding
		};
		const toggleRect = this.$toggle.getBoundingClientRect();
		const tooltipRect = this.measureTooltip();
		const spaces = {
			top: toggleRect.top - viewport.top,
			right: viewport.right - toggleRect.right,
			bottom: viewport.bottom - toggleRect.bottom,
			left: toggleRect.left - viewport.left
		};

		const order = window.innerWidth <= 767
			? ["bottom", "top", "right", "left"]
			: ["top", "right", "bottom", "left"];

		const preferred = this.$module.dataset.placement;
		const fit = (placement) =>
			placement === "top" || placement === "bottom"
				? spaces[placement] >= tooltipRect.height + offset
				: spaces[placement] >= tooltipRect.width + offset;

		let placement = preferred && fit(preferred) ? preferred : order.find(fit);
		if (!placement) {
			placement = order.reduce((best, current) =>
				spaces[current] > spaces[best] ? current : best
			);
		}

		let top = toggleRect.bottom + offset;
		let left = toggleRect.left + (toggleRect.width - tooltipRect.width) * 0.5;

		if (placement === "top") {
			top = toggleRect.top - tooltipRect.height - offset;
		}
		if (placement === "right") {
			top = toggleRect.top + (toggleRect.height - tooltipRect.height) * 0.5;
			left = toggleRect.right + offset;
		}
		if (placement === "left") {
			top = toggleRect.top + (toggleRect.height - tooltipRect.height) * 0.5;
			left = toggleRect.left - tooltipRect.width - offset;
		}

		const minLeft = viewport.left;
		const maxLeft = Math.max(minLeft, viewport.right - tooltipRect.width);
		const minTop = viewport.top;
		const maxTop = Math.max(minTop, viewport.bottom - tooltipRect.height);

		left = Math.min(maxLeft, Math.max(minLeft, left));
		top = Math.min(maxTop, Math.max(minTop, top));

		this.$tooltip.dataset.placement = placement;
		this.$tooltip.style.top = `${top}px`;
		this.$tooltip.style.left = `${left}px`;

		if (placement === "top" || placement === "bottom") {
			const arrowX = Math.max(
				arrowPadding,
				Math.min(
					tooltipRect.width - arrowPadding,
					toggleRect.left + toggleRect.width * 0.5 - left
				)
			);
			this.$tooltip.style.setProperty("--bc-tooltip-arrow-x", `${arrowX}px`);
			this.$tooltip.style.removeProperty("--bc-tooltip-arrow-y");
			return;
		}

		const arrowY = Math.max(
			arrowPadding,
			Math.min(
				tooltipRect.height - arrowPadding,
				toggleRect.top + toggleRect.height * 0.5 - top
			)
		);
		this.$tooltip.style.setProperty("--bc-tooltip-arrow-y", `${arrowY}px`);
		this.$tooltip.style.removeProperty("--bc-tooltip-arrow-x");
	}

	measureTooltip() {
		const currentVisibility = this.$tooltip.style.visibility;
		const currentTop = this.$tooltip.style.top;
		const currentLeft = this.$tooltip.style.left;

		this.$tooltip.style.visibility = "hidden";
		this.$tooltip.style.top = "0px";
		this.$tooltip.style.left = "0px";

		const rect = this.$tooltip.getBoundingClientRect();

		this.$tooltip.style.visibility = currentVisibility;
		this.$tooltip.style.top = currentTop;
		this.$tooltip.style.left = currentLeft;

		return rect;
	}
}
